import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { getAdminLoad } from "@/lib/api/admin/loads";
import {
  getAllLoadTrades,
  getLoadNegotiations,
  getTradeNegotiations,
  type FullNegotiation,
  type LoadTrade,
} from "@/lib/api/negotiations";
import { queryKeys } from "@/lib/query-keys";
import { getLoadStatusLabel } from "@/lib/api/loads";
import { formatDate } from "@/lib/formatting";
import { getLocalizedName } from "@/lib/localization";
import { getCarrierColor } from "@/lib/carrier-colors";
import { TradeCard } from "@/components/negotiations/trade-card";
import {
  NegotiationMessage,
  type NegotiationMessageProps,
} from "@/components/negotiations/negotiation-message";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ErrorState, Text } from "@/components/ui/text";
import { Icon } from "@/components/ui/icons";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { Skeleton } from "@/components/ui/skeleton";
import { EntityStatusBadge } from "@/components/shared/entity-status-badge";
import { Surface } from "@/components/ui/surface";
import { VStack } from "@/components/ui/v-stack";
import { FormGrid } from "@/components/ui/form-grid";
import { HStack } from "@/components/ui/h-stack";
import { StickyBar } from "@/components/ui/sticky-bar";
import { fromIntlLanguage } from "@/lib/i18n/config";

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReducedMotion(mediaQuery.matches);

    onChange();
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  return prefersReducedMotion;
}

function RevealBlock({
  index,
  children,
}: {
  index: number;
  children: React.ReactNode;
}) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setVisible(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <VStack
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transitionProperty: "opacity, transform",
        transitionDuration: prefersReducedMotion ? "120ms" : "280ms",
        transitionTimingFunction: "ease-out",
        transitionDelay: prefersReducedMotion ? "0ms" : `${index * 70}ms`,
      }}
    >
      {children}
    </VStack>
  );
}

// Helper to determine if a negotiation is from a carrier
function isNegotiationFromCarrier(
  neg: FullNegotiation,
  trade?: LoadTrade,
): boolean {
  const tradeToCheck = trade || neg.trade;
  if (!tradeToCheck) return false;
  return neg.sender.id === tradeToCheck.sender.id;
}

function AdminLoadTradesPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const params = Route.useParams();
  const id = params?.id;
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tAdminLoadApprovalDetails } = useTranslation(
    "admin.loadApprovalDetails",
  );
  const { t: tCommon } = useTranslation("common");
  const { t: tLoads } = useTranslation("loads");
  const { t: tNegotiations } = useTranslation("negotiations");

  const [userSelectedTradeId, setSelectedTradeId] = useState<string | null>(
    null,
  );
  const [historyMode, setHistoryMode] = useState<"carrier" | "all">("all");

  // ── Data queries ──────────────────────────────────────────────────────
  const loadQuery = useQuery({
    queryKey: queryKeys.admin.load(id!),
    queryFn: () => getAdminLoad(id!),
    enabled: !!id,
  });
  const load = loadQuery.data ?? null;

  const tradesQuery = useQuery({
    queryKey: queryKeys.admin.loadTrades(id!),
    queryFn: () => getAllLoadTrades(id!),
    enabled: !!id,
  });
  const tradesData = tradesQuery.data;
  const trades = useMemo(() => tradesData ?? [], [tradesData]);

  // Auto-select first trade if user hasn't picked one
  const selectedTradeId = userSelectedTradeId ?? trades[0]?.id ?? null;

  const carrierNegotiationsQuery = useQuery({
    queryKey: ["trade-negotiations", selectedTradeId],
    queryFn: () => getTradeNegotiations(selectedTradeId!),
    enabled: historyMode === "carrier" && !!selectedTradeId,
  });
  const negotiations = useMemo(
    () => carrierNegotiationsQuery.data ?? [],
    [carrierNegotiationsQuery.data],
  );

  const allNegotiationsQuery = useQuery({
    queryKey: ["load-negotiations", load?.id],
    queryFn: async () => {
      const data = await getLoadNegotiations(load!.id);
      data.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      return data;
    },
    enabled: historyMode === "all" && !!load?.id,
  });
  const allNegotiations = useMemo(
    () => allNegotiationsQuery.data ?? [],
    [allNegotiationsQuery.data],
  );

  const loading = loadQuery.isLoading || tradesQuery.isLoading;
  const loadingNegotiations = carrierNegotiationsQuery.isLoading;
  const loadingAllNegotiations = allNegotiationsQuery.isLoading;
  const errorKind = loadQuery.isError || tradesQuery.isError ? "load" : null;

  const selectedTrade = useMemo(
    () => trades.find((t) => t.id === selectedTradeId) ?? null,
    [selectedTradeId, trades],
  );
  const negotiationMessageTranslations = useMemo<NegotiationMessageProps["t"]>(
    () => ({
      common: {
        you: tCommon("you"),
        cancel: tCommon("cancel"),
      },
      negotiations: {
        accept: tNegotiations("accept"),
        selectTagOptional: tNegotiations("selectTagOptional"),
        noTag: tNegotiations("noTag"),
        counter: tNegotiations("counter"),
        decline: tNegotiations("decline"),
        enterAmount: tNegotiations("enterAmount"),
        send: tNegotiations("send"),
      },
    }),
    [tCommon, tNegotiations],
  );

  if (loading) {
    return (
      <VStack gap="xl" data-testid="admin-load-trades-loading">
        <VStack gap="sm">
          <Skeleton className="h-8 w-40" />
          <Card>
            <HStack gap="lg">
              <Skeleton className="h-10 w-24" />
              <VStack gap="sm">
                <Skeleton className="h-6 w-64" />
                <Skeleton className="h-4 w-40" />
              </VStack>
              <Skeleton rounded="full" className="ml-auto h-7 w-24" />
            </HStack>
          </Card>
        </VStack>
        <FormGrid cols="lg-3" gap="xl">
          <Card padding="none" className="lg:col-span-1">
            <Surface border="bottom" className="p-4">
              <Text weight="semibold">{tNegotiations("offers")}</Text>
            </Surface>
            <CardContent className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Surface key={index} border="default" rounded="lg" padding="md">
                  <Skeleton className="mb-2 h-5 w-40" />
                  <Skeleton className="h-4 w-28" />
                </Surface>
              ))}
            </CardContent>
          </Card>
          <Card padding="none" className="lg:col-span-2">
            <Surface border="bottom" className="p-4">
              <Text weight="semibold">
                {tNegotiations("negotiationHistory")}
              </Text>
            </Surface>
            <VStack gap="lg" padding="md">
              {Array.from({ length: 5 }).map((_, index) => (
                <HStack key={index}>
                  <Surface
                    bg="container"
                    border="default"
                    rounded="2xl"
                    padding="md"
                    className="max-w-[80%]"
                  >
                    <VStack gap="sm">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-4 w-24" />
                    </VStack>
                  </Surface>
                </HStack>
              ))}
            </VStack>
          </Card>
        </FormGrid>
      </VStack>
    );
  }

  if (errorKind || !load) {
    return (
      <ErrorState
        title={tCommon("errorLoadingData")}
        action={
          <Button
            color="neutral"
            variant="outline"
            onClick={() => {
              void loadQuery.refetch();
              void tradesQuery.refetch();
            }}
          >
            {tCommon("retry")}
          </Button>
        }
        className="py-10"
      />
    );
  }

  return (
    <VStack gap="xl" data-testid="admin-load-trades-page">
      <RevealBlock index={0}>
        <PageNavigationHeader
          contextLabel={tAdminLoadApprovalDetails("titleFallback")}
          title={`${load.load_id}: ${getLocalizedName(load.load_type, language)}`}
          metadata={`${tLoads("created")} ${formatDate(load.created_at, language)}`}
          status={
            <EntityStatusBadge entityType="load" status={load.status}>
              {getLoadStatusLabel(load.status, tLoads)}
            </EntityStatusBadge>
          }
          onBack={() => {
            if (typeof window !== "undefined" && window.history.length > 1) {
              router.history.back();
              return;
            }
            void navigate({ to: "/admin/loads/$id", params: { id: load.id } });
          }}
          backAriaLabel={tCommon("back")}
          contextAriaLabel={tAdminLoadApprovalDetails("titleFallback")}
        />
      </RevealBlock>

      <RevealBlock index={1}>
        <FormGrid
          cols="lg-3"
          gap="xl"
          className="h-[calc(100vh-250px)] min-h-[500px]"
        >
          <Card
            padding="none"
            className="lg:col-span-1 overflow-hidden flex flex-col"
          >
            <StickyBar z="base">
              <Text weight="semibold">
                {tNegotiations("offers")} ({trades.length})
              </Text>
            </StickyBar>
            <CardContent className="flex-1 overflow-y-auto">
              {trades.length === 0 ? (
                <HStack justify="center" className="py-10">
                  <Text color="muted">{tNegotiations("noOffersYet")}</Text>
                </HStack>
              ) : (
                <VStack gap="sm">
                  {trades.map((trade) => (
                    <TradeCard
                      key={trade.id}
                      trade={trade}
                      isSelected={trade.id === selectedTradeId}
                      onClick={() => {
                        setSelectedTradeId(trade.id);
                        setHistoryMode("carrier");
                      }}
                      language={language}
                      t={tCommon}
                      isCarrierView={false}
                    />
                  ))}
                </VStack>
              )}
            </CardContent>
          </Card>

          <Card
            padding="none"
            className="lg:col-span-2 overflow-hidden flex flex-col"
          >
            <StickyBar z="base">
              <HStack justify="between" gap="none">
                <HStack>
                  <Icon.Bell size="sm" color="muted" />
                  <Text weight="semibold">
                    {tNegotiations("negotiationHistory")}
                  </Text>
                </HStack>

                <Surface bg="background" rounded="lg" className="p-0.5">
                  <HStack gap="xs">
                    <Button
                      data-testid="history-mode-carrier"
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistoryMode("carrier")}
                      className={`px-2 py-1 text-xs rounded-md ${
                        historyMode === "carrier"
                          ? "bg-[color:var(--surface-container)] text-[color:var(--on-surface)] shadow-sm"
                          : "text-[color:var(--on-surface-variant)]"
                      }`}
                    >
                      {tNegotiations("selected")}
                    </Button>
                    <Button
                      data-testid="history-mode-all"
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistoryMode("all")}
                      className={`px-2 py-1 text-xs rounded-md ${
                        historyMode === "all"
                          ? "bg-[color:var(--surface-container)] text-[color:var(--on-surface)] shadow-sm"
                          : "text-[color:var(--on-surface-variant)]"
                      }`}
                    >
                      {tNegotiations("allPartners")}
                    </Button>
                  </HStack>
                </Surface>
              </HStack>
              <Text size="xs" color="muted" className="mt-1">
                {historyMode === "carrier" && selectedTrade
                  ? `${selectedTrade.sender.name} → ${selectedTrade.receiver.name} · ${negotiations.length} ${tNegotiations("messages")}`
                  : historyMode === "all"
                    ? `${tNegotiations("allPartners")} · ${allNegotiations.length} ${tNegotiations("messages")}`
                    : tNegotiations("selectPartnerToView")}
              </Text>
            </StickyBar>

            {historyMode === "carrier" ? (
              loadingNegotiations ? (
                <VStack padding="md">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Surface
                      key={index}
                      border="default"
                      rounded="2xl"
                      padding="md"
                      className="max-w-[80%]"
                    >
                      <Skeleton className="mb-2 h-4 w-36" />
                      <Skeleton className="h-4 w-48" />
                    </Surface>
                  ))}
                </VStack>
              ) : !selectedTradeId ? (
                <VStack gap="none" padding="lg">
                  <Text color="muted">
                    {tNegotiations("selectOfferToViewHistory")}
                  </Text>
                </VStack>
              ) : negotiations.length === 0 ? (
                <VStack gap="none" padding="lg">
                  <Text color="muted">{tNegotiations("noMessagesYet")}</Text>
                </VStack>
              ) : (
                <ScrollArea reverse padding="md" className="gap-4">
                  {negotiations.map((n) => {
                    const tradeForNeg =
                      trades.find((t) => t.id === n.trade.id) ?? undefined;
                    const isFromCarrier = isNegotiationFromCarrier(
                      n,
                      tradeForNeg,
                    );
                    return (
                      <NegotiationMessage
                        key={n.id}
                        negotiation={n}
                        isOutgoing={!isFromCarrier}
                        language={language}
                        carrierColor={
                          isFromCarrier && tradeForNeg
                            ? getCarrierColor(tradeForNeg.sender.id, [
                                ...new Set(trades.map((t) => t.sender.id)),
                              ])
                            : undefined
                        }
                        actions={null}
                        hideCurrentUserLabel={true}
                        t={negotiationMessageTranslations}
                      />
                    );
                  })}
                </ScrollArea>
              )
            ) : loadingAllNegotiations ? (
              <VStack padding="md">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Surface
                    key={index}
                    border="default"
                    rounded="2xl"
                    padding="md"
                    className="max-w-[80%]"
                  >
                    <Skeleton className="mb-2 h-4 w-36" />
                    <Skeleton className="h-4 w-48" />
                  </Surface>
                ))}
              </VStack>
            ) : allNegotiations.length === 0 ? (
              <VStack gap="none" padding="lg">
                <Text color="muted">{tNegotiations("noMessagesYet")}</Text>
              </VStack>
            ) : (
              <ScrollArea
                reverse
                padding="md"
                data-testid="negotiations-messages-list"
                className="gap-4"
              >
                {(() => {
                  const carrierIds = [
                    ...new Set(trades.map((t) => t.sender.id)),
                  ];

                  const handleReplyToClick = (negotiationId: string) => {
                    const element = document.querySelector(
                      `[data-testid="negotiations-message-${negotiationId}"]`,
                    );
                    if (element) {
                      element.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                      element.classList.add(
                        "ring-2",
                        "ring-[color:var(--primary)]",
                        "ring-offset-2",
                      );
                      setTimeout(() => {
                        element.classList.remove(
                          "ring-2",
                          "ring-[color:var(--primary)]",
                          "ring-offset-2",
                        );
                      }, 2000);
                    }
                  };

                  return allNegotiations.map((neg) => {
                    const tradeForNeg =
                      trades.find((t) => t.id === neg.trade.id) ?? undefined;
                    const isFromCarrier = isNegotiationFromCarrier(
                      neg,
                      tradeForNeg,
                    );
                    const carrierColor =
                      isFromCarrier && tradeForNeg
                        ? getCarrierColor(tradeForNeg.sender.id, carrierIds)
                        : undefined;

                    return (
                      <NegotiationMessage
                        key={neg.id}
                        negotiation={neg}
                        isOutgoing={!isFromCarrier}
                        language={language}
                        carrierColor={carrierColor}
                        onReplyToClick={
                          neg.previous_negotiation
                            ? handleReplyToClick
                            : undefined
                        }
                        actions={null}
                        hideCurrentUserLabel={true}
                        t={negotiationMessageTranslations}
                      />
                    );
                  });
                })()}
              </ScrollArea>
            )}
          </Card>
        </FormGrid>
      </RevealBlock>
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/loads/$id/trades")({
  component: AdminLoadTradesPage,
});
